import {uniqueDestinationOptions} from "../../utils/destinationOptions";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Airport, ConnectionMode, DestinationCountry, TravelProfile } from "../../models/flightSearchData";
import { AirportResponse, AirportService, ReachableAirportResponse } from "../../services/AirportService";
import { useTranslation } from "../../i18n/LanguageProvider";

import {matchesCountry, normalizeSearch} from "../../utils/countrySearch";
import {TravelRequirementsService} from "../../services/TravelRequirementsService";
import PassportSelector from "../travel/PassportSelector";

interface DestinationSelectorProps {
  travelProfile?: TravelProfile | null;
  setTravelProfile: (profile: TravelProfile | null) => void;
  departureAirportCodes: string[];
  connectionMode: ConnectionMode;
  setConnectionMode: (connectionMode: ConnectionMode) => void;
  selectedAirports: Airport[];
  selectedCountries: DestinationCountry[];
  setSelectedAirports: (airports: Airport[]) => void;
  setSelectedCountries: (countries: DestinationCountry[]) => void;
  onClose: () => void;
}

enum DestinationTab {
  Airports = "airports",
  Countries = "countries",
}

const skeletonDestinations = [
  { city: "Palma", code: "PMI" },
  { city: "Barcelona", code: "BCN" },
  { city: "Rom", code: "FCO" },
  { city: "Lissabon", code: "LIS" },
  { city: "Malaga", code: "AGP" },
  { city: "Mailand", code: "BGY" },
  { city: "Porto", code: "OPO" },
  { city: "Valencia", code: "VLC" },
  { city: "Alicante", code: "ALC" },
  { city: "Zadar", code: "ZAD" },
  { city: "Sofia", code: "SOF" },
  { city: "London", code: "STN" },
];

const MAX_ONE_STOP_DESTINATIONS = 3;

const DestinationSelector: React.FC<DestinationSelectorProps> = ({
  travelProfile,
  setTravelProfile,
  departureAirportCodes,
  connectionMode,
  setConnectionMode,
  selectedAirports,
  selectedCountries,
  setSelectedAirports,
  setSelectedCountries,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<DestinationTab>(DestinationTab.Airports);
  const [query, setQuery] = useState("");
  const [airports, setAirports] = useState<ReachableAirportResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const { t, locale } = useTranslation();
  const departureAirportKey = useMemo(
    () => departureAirportCodes.map((code) => code.trim().toUpperCase()).sort().join(","),
    [departureAirportCodes]
  );
  const normalizedDepartureAirportCodes = useMemo(
    () => departureAirportKey.split(",").filter(Boolean),
    [departureAirportKey]
  );
  const isOneStopMode = connectionMode === ConnectionMode.DirectAndOneStop;
  const selectedDestinationCount = selectedAirports.length + selectedCountries.length;
  const hasReachedOneStopDestinationLimit =
    isOneStopMode && selectedDestinationCount >= MAX_ONE_STOP_DESTINATIONS;

  useEffect(() => {
    if (!isOneStopMode) return;

    if (activeTab === DestinationTab.Countries) {
      setActiveTab(DestinationTab.Airports);
    }

    if (selectedCountries.length > 0) {
      setSelectedCountries([]);
    }

    if (selectedAirports.length > MAX_ONE_STOP_DESTINATIONS) {
      setSelectedAirports(selectedAirports.slice(0, MAX_ONE_STOP_DESTINATIONS));
    }
  }, [
    activeTab,
    isOneStopMode,
    selectedAirports,
    selectedCountries,
    setSelectedAirports,
    setSelectedCountries,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadAirports = async () => {
      if (normalizedDepartureAirportCodes.length === 0) {
        setAirports([]);
        setLoadError(false);
        setIsLoading(false);
        setIsContentVisible(false);
        return;
      }

      const hasCachedDestinations = AirportService.hasCachedReachableDestinationAirports(
        normalizedDepartureAirportCodes,
        connectionMode
      );
      setIsLoading(!hasCachedDestinations);
      setLoadError(false);
      setIsContentVisible(hasCachedDestinations);
      try {
        const result = await AirportService.getReachableDestinationAirportsForDepartures(
          normalizedDepartureAirportCodes,
          connectionMode
        );
        if (isMounted) {
          setAirports(result);
          if (hasCachedDestinations) {
            setIsContentVisible(true);
            return;
          }
          window.setTimeout(() => {
            if (isMounted) {
              setIsContentVisible(true);
            }
          }, 80);
        }
      } catch (error) {
        console.error(t("destination.error"), error);
        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAirports();

    return () => {
      isMounted = false;
    };
  }, [connectionMode, departureAirportKey, normalizedDepartureAirportCodes, t]);

  const visaFilterActive = !!travelProfile && (travelProfile.visaFreeOnly || !!travelProfile.easyVisaOnly);
  const visaKey = visaFilterActive ? JSON.stringify([travelProfile, departureAirportKey, connectionMode]) : "";
  const [visaOptions, setVisaOptions] = useState<{key: string; allowed: Set<string>; error: boolean} | null>(null);
  const [visaRetry, setVisaRetry] = useState(0);
  useEffect(() => {
    if (!visaKey || !travelProfile) return;
    let cancelled = false;
    setVisaOptions(null);
    TravelRequirementsService.eligibleDestinations(travelProfile, normalizedDepartureAirportCodes, connectionMode)
      .then(allowed => {if (!cancelled) setVisaOptions({key: visaKey, allowed, error: false});})
      .catch(() => {if (!cancelled) setVisaOptions({key: visaKey, allowed: new Set(), error: true});});
    return () => {cancelled = true;};
    // visaKey includes every input to this request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visaKey, visaRetry]);
  const visaLoading = visaFilterActive && visaOptions?.key !== visaKey;
  const visaError = visaFilterActive && visaOptions?.key === visaKey && visaOptions.error;
  const eligibleAirports = useMemo(() => !visaFilterActive ? airports :
    visaOptions?.key === visaKey ? airports.filter(airport => visaOptions.allowed.has(airport.iata)) : [],
    [airports, visaFilterActive, visaOptions, visaKey]);

  const destinationOptions = useMemo(() => connectionMode === ConnectionMode.DirectAndOneStop
    ? uniqueDestinationOptions(eligibleAirports, selectedAirports.map(airport => airport.code))
    : eligibleAirports, [eligibleAirports, connectionMode, selectedAirports]);

  const countries = useMemo(() => {
    const byCode = new Map<string, DestinationCountry>();
    const names = new Intl.DisplayNames([locale], {type: "region"});
    eligibleAirports.forEach((airport) => {
      byCode.set(airport.country_code, {
        code: airport.country_code,
        name: names.of(airport.country_code.toUpperCase()) || airport.country,
      });
    });

    return Array.from(byCode.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleAirports, locale]);

  useEffect(() => {
    if (normalizedDepartureAirportCodes.length === 0 || airports.length === 0 || visaLoading || visaError) return;

    const reachableAirportCodes = new Set(destinationOptions.map((airport) => airport.iata));
    const reachableCountryCodes = new Set(eligibleAirports.map((airport) => airport.country_code));

    const validSelectedAirports = selectedAirports.filter((airport) =>
      reachableAirportCodes.has(airport.code)
    );
    const validSelectedCountries = selectedCountries.filter((country) =>
      reachableCountryCodes.has(country.code)
    );

    if (validSelectedAirports.length !== selectedAirports.length) {
      setSelectedAirports(validSelectedAirports);
    }

    if (validSelectedCountries.length !== selectedCountries.length) {
      setSelectedCountries(validSelectedCountries);
    }
  }, [
    airports,
    eligibleAirports,
    destinationOptions,
    visaLoading,
    visaError,
    normalizedDepartureAirportCodes,
    selectedAirports,
    selectedCountries,
    setSelectedAirports,
    setSelectedCountries,
  ]);

  const normalizedQuery = normalizeSearch(query);

  const filteredAirports = useMemo(() => {
    return destinationOptions
      .filter((airport) => {
        if (!normalizedQuery) return true;
        return (
          normalizeSearch(airport.city).includes(normalizedQuery) ||
          normalizeSearch(airport.name).includes(normalizedQuery) ||
          normalizeSearch(airport.iata).includes(normalizedQuery) ||
          matchesCountry(airport.country_code, normalizedQuery, airport.country)
        );
      })
      .slice(0, 60);
  }, [destinationOptions, normalizedQuery]);

  const filteredCountries = useMemo(() => {
    return countries.filter((country) => {
      if (!normalizedQuery) return true;
      return (
        matchesCountry(country.code, normalizedQuery, country.name)
      );
    });
  }, [countries, normalizedQuery]);

  const toggleAirport = (airport: AirportResponse) => {
    const selected = selectedAirports.some((item) => item.code === airport.iata);
    if (selected) {
      setSelectedAirports(selectedAirports.filter((item) => item.code !== airport.iata));
    } else if (!hasReachedOneStopDestinationLimit) {
      setSelectedAirports([...selectedAirports, { city: airport.city, code: airport.iata }]);
    }
  };

  const formatViaAirports = (airport: ReachableAirportResponse) => {
    if (airport.reachability === "direct") return t("connection.direct");
    if (!airport.viaAirports.length) return t("connection.oneStop");

    return t("connection.via", {
      airport: `${airport.viaAirports[0].city} ${airport.viaAirports[0].iata}`,
    });
  };

  const toggleCountry = (country: DestinationCountry) => {
    if (isOneStopMode) return;

    const selected = selectedCountries.some((item) => item.code === country.code);
    if (selected) {
      setSelectedCountries(selectedCountries.filter((item) => item.code !== country.code));
    } else {
      setSelectedCountries([...selectedCountries, country]);
    }
  };

  const clearSelection = () => {
    setSelectedAirports([]);
    setSelectedCountries([]);
  };

  const selectConnectionMode = (nextConnectionMode: ConnectionMode) => {
    if (nextConnectionMode === connectionMode) return;
    if (nextConnectionMode === ConnectionMode.DirectAndOneStop) {
      setActiveTab(DestinationTab.Airports);
      setSelectedCountries([]);
      setSelectedAirports(selectedAirports.slice(0, MAX_ONE_STOP_DESTINATIONS));
    }
    setConnectionMode(nextConnectionMode);
  };

  return (
    <div className="selector-panel p-4 sm:p-6 bg-[#FBFBFF] shadow-card rounded-2xl w-full transition-smooth will-change-transform">
      <div className="flex flex-wrap gap-3 justify-between items-start mb-4 transition-smooth">
        <h2 className="selector-heading text-accent font-semibold text-sm">{t("destination.title")}</h2>
        <div className="flex items-center gap-3">
          {(selectedAirports.length > 0 || selectedCountries.length > 0) && (
            <button onClick={clearSelection} className="text-sm text-[#A0A0A0] hover:underline">
              {t("destination.clear")}
            </button>
          )}
          <button
            onClick={onClose}
            data-selector-close
            aria-label={t("destination.close")}
            title={t("destination.close")}
            className="flex h-8 w-8 items-center justify-center text-accent opacity-60 transition hover:opacity-100"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12" />
              <path d="M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>

      <div className="mb-4 max-w-2xl rounded-2xl bg-[#F3F1FF] p-1.5 shadow-inner transition-smooth">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => selectConnectionMode(ConnectionMode.Direct)}
            className={classNames(
              "rounded-xl px-4 py-2 text-sm font-bold transition-all duration-300 ease-in-out",
              connectionMode === ConnectionMode.Direct
                ? "bg-white text-accent shadow-sm"
                : "text-[#8D72E8] hover:text-accent"
            )}
          >
            {t("connection.directOnly")}
          </button>
          <button
            onClick={() => selectConnectionMode(ConnectionMode.DirectAndOneStop)}
            className={classNames(
              "rounded-xl px-4 py-2 text-sm font-bold transition-all duration-300 ease-in-out",
              connectionMode === ConnectionMode.DirectAndOneStop
                ? "bg-white text-accent shadow-sm"
                : "text-[#8D72E8] hover:text-accent"
            )}
          >
            {t("connection.directAndOneStop")}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 transition-smooth">
        <button
          onClick={() => setActiveTab(DestinationTab.Airports)}
          className={classNames(
            "px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out",
            activeTab === DestinationTab.Airports
              ? "bg-[#4B4B4B] text-white"
              : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
          )}
        >
          {t("common.airports")}
        </button>
        <button
          onClick={() => setActiveTab(DestinationTab.Countries)}
          disabled={isOneStopMode}
          title={isOneStopMode ? t("destination.oneStopCountriesDisabled") : undefined}
          className={classNames(
            "px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out",
            isOneStopMode && "cursor-not-allowed opacity-45",
            activeTab === DestinationTab.Countries
              ? "bg-[#4B4B4B] text-white"
              : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
          )}
        >
          {t("common.countries")}
        </button>
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={activeTab === DestinationTab.Airports ? t("common.searchAirport") : t("common.searchCountry")}
        className="w-full max-w-2xl rounded-xl border border-[#D8D8D8] bg-white px-4 py-3 mb-4 text-sm text-[#4B4B4B] outline-none transition-smooth focus:border-[#7745F5]"
      />

      {(selectedAirports.length > 0 || selectedCountries.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-4 max-w-2xl transition-smooth">
          {selectedCountries.map((country) => (
            <button
              key={country.code}
              onClick={() => toggleCountry(country)}
              className="rounded-xl px-3 py-1.5 bg-[#F3F1FF] text-accent text-sm font-bold transition-smooth hover:bg-[#E8E0FF]"
            >
              {country.code} {country.name} x
            </button>
          ))}
          {selectedAirports.map((airport) => (
            <button
              key={airport.code}
              onClick={() =>
                setSelectedAirports(selectedAirports.filter((item) => item.code !== airport.code))
              }
              className="rounded-xl px-3 py-1.5 bg-[#F3F1FF] text-accent text-sm font-bold transition-smooth hover:bg-[#E8E0FF]"
            >
              {airport.city} {airport.code} x
            </button>
          ))}
        </div>
      )}

      {normalizedDepartureAirportCodes.length === 0 ? (
        <div className="text-sm text-[#A0A0A0]">{t("destination.requireDeparture")}</div>
      ) : visaLoading ? (
        <p role="status" className="py-4 text-sm text-gray-600">{t("visa.loadingDestinations")}</p>
      ) : visaError ? (
        <div role="alert" className="py-4 text-sm text-gray-600">
          <p>{t("visa.destinationsError")}</p>
          <button type="button" className="min-h-[44px] font-semibold text-accent" onClick={() => setVisaRetry(value => value + 1)}>{t("visa.retry")}</button>
        </div>
      ) : isLoading ? (
        <div className="flex min-h-40 flex-wrap content-start gap-3 max-w-2xl pr-1 transition-smooth">
          {skeletonDestinations.map((destination) => (
            <div
              key={destination.code}
              className="flex items-center gap-2 rounded-xl bg-[#EAEAEA] px-4 py-2 text-sm font-bold opacity-70 animate-pulse transition-smooth"
            >
              <span className="text-[#535353] blur-sm">{destination.city}</span>
              <span className="text-[#9C9C9C] blur-sm">{destination.code}</span>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div className="text-sm text-[#A0A0A0]">
          {t("destination.error")}
        </div>
      ) : (
        <div className="flex min-h-40 flex-wrap content-start gap-3 max-w-2xl max-h-72 overflow-y-auto pr-1 transition-smooth">
          {activeTab === DestinationTab.Airports &&
            filteredAirports.map((airport) => {
              const selected = selectedAirports.some((item) => item.code === airport.iata);
              const disabled = !selected && hasReachedOneStopDestinationLimit;
              return (
                <button
                  key={airport.iata}
                  aria-pressed={selected}
                  onClick={() => toggleAirport(airport)}
                  disabled={disabled}
                  className={classNames(
                    "rounded-xl px-4 py-2 hover:scale-105 font-bold text-sm transition-all duration-500 ease-out flex items-center gap-2",
                    isContentVisible ? "opacity-100 blur-0" : "opacity-40 blur-sm",
                    disabled && "cursor-not-allowed hover:scale-100",
                    selected
                      ? "bg-[#4B4B4B] hover:bg-[#3F3F3F]"
                      : "bg-[#EAEAEA] hover:bg-[#D5D5D5]"
                  )}
                >
                  <span className="flex flex-col items-start leading-tight">
                    <span className={selected ? "text-white" : "text-[#535353]"}>
                      {airport.city}
                    </span>
                    {connectionMode === ConnectionMode.DirectAndOneStop && (
                      <span className={selected ? "text-[10px] text-[#D7D7D7]" : "text-[10px] text-[#9C9C9C]"}>
                        {formatViaAirports(airport)}
                      </span>
                    )}
                  </span>
                  <span className={selected ? "text-[#D7D7D7]" : "text-[#9C9C9C]"}>{airport.iata}</span>
                </button>
              );
            })}

          {activeTab === DestinationTab.Countries &&
            filteredCountries.map((country) => {
              const selected = selectedCountries.some((item) => item.code === country.code);
              return (
                <button
                  key={country.code}
                  aria-pressed={selected}
                  onClick={() => toggleCountry(country)}
                  className={classNames(
                    "rounded-xl px-4 py-2 hover:scale-105 font-bold text-sm transition-all duration-500 ease-out flex items-center gap-2",
                    isContentVisible ? "opacity-100 blur-0" : "opacity-40 blur-sm",
                    selected
                      ? "bg-[#4B4B4B] hover:bg-[#3F3F3F]"
                      : "bg-[#EAEAEA] hover:bg-[#D5D5D5]"
                  )}
                >
                  <span className={selected ? "text-[#D7D7D7]" : "text-[#9C9C9C]"}>{country.code}</span>
                  <span className={selected ? "text-white" : "text-[#535353]"}>{country.name}</span>
                </button>
              );
            })}

          {activeTab === DestinationTab.Airports && filteredAirports.length === 0 && (
            <div className="text-sm text-[#A0A0A0]">{t("destination.emptyAirports")}</div>
          )}

          {activeTab === DestinationTab.Countries && filteredCountries.length === 0 && (
            <div className="text-sm text-[#A0A0A0]">{t("destination.emptyCountries")}</div>
          )}
        </div>
      )}
      <PassportSelector value={travelProfile} onChange={setTravelProfile} />
    </div>
  );
};

export default DestinationSelector;
